<svelte:options customElement="dst-useractions" />

<script lang="ts">
	import { baseUrls } from '@/config.ts';
	import * as Auth from '@/lib/authentication.ts';
	import { changeState, register as registerClosables } from '@/lib/closable.ts';
	import { Logger } from '@/lib/log.ts';
	import { pushToDataLayer, trackUserAction } from '@/lib/tracking.ts';
	import { removeNewNotificationsIndicator } from '@/lib/utils.ts';
	import notifications from '@/stores/notification_store.ts';
	import { loginState } from '@derstandard/login-state';
	import {
		dstAbo,
		dstHeaderAccount,
		dstHeaderAccountAbo,
		dstHeaderAccountSupporter,
		dstHeaderKey,
		dstSupporterThickColor,
	} from '@derstandard/styling-toolkit/src/svg/lib.js';
	import { createMutationObserver } from '@derstandard/utils';
	import { mdiNewspaper } from '@mdi/js';
	import { afterUpdate } from 'svelte';

	let root, userActionsFlyout;

	const log = Logger('header.DstUseractions');

	const baseUrl =
		window.DERSTANDARD?.pageConfig?.edition === 'de' ? baseUrls.editorialweb_de : baseUrls.editorialweb_at;
	export let appid;
	export let hasnotifications;

	let profileUrls = {
		followees: `${baseUrls.legacy_profile}/postings`,
		postings: `${baseUrls.legacy_profile}/postings`,
		settings: `${baseUrls.legacy_profile}/einstellungen`,
	};

	$: {
		if (root && hasnotifications && $notifications?.hasNewNotifications) {
			log.debug("user has new notifications. adding 'notifications-active' class.");
			(root.getRootNode() as ShadowRoot).host.classList.add('notifications-active');
		} else if (root && hasnotifications) {
			log.debug("user does not have new notifications. removing 'notifications-active' class.");
			(root.getRootNode() as ShadowRoot).host.classList.remove('notifications-active');
		}
	}
	$: ariaExpanded = false;

	const { disconnect, observe } = createMutationObserver({
		onChanged: (mutation) => {
			const target = mutation.target as Element;

			if (!target.getAttribute('id') === 'app-useractions') {
				return;
			}

			const attrName = mutation.attributeName;
			const newValue = target.getAttribute(attrName);

			ariaExpanded = newValue === 'true';
		},
	});

	afterUpdate(async () => {
		registerClosables(root);
	});

	loginState.subscribe(async (state) => {
		await updateProfileLinks();

		if (!state.isLoggedIn) {
			return;
		}

		void (await import('../DstCounter/DstCounter.webcomponent.svelte')).default;
	});

	/**
	 * Click-handler for login
	 */
	function handleLogin() {
		Auth.signIn();
	}

	/**
	 * Click-handler for logout
	 */
	function handleLogout() {
		Auth.signOut();

		changeState(userActionsFlyout, false);

		pushToDataLayer({
			event: 'tracking.userAction',
			userActionType: 'registration_pl_signedout',
		});
	}

	/**
	 * Sets profile links based on user session.
	 */
	async function updateProfileLinks() {
		const memberId = $loginState.payload?.mId;

		profileUrls = {
			followees: memberId ? `${baseUrl}/profil/${memberId}/ich-folge` : `${baseUrls.legacy_profile}/postings`,
			postings: memberId ? `${baseUrl}/profil/${memberId}` : `${baseUrls.legacy_profile}/postings`,
			settings: memberId ? `${baseUrl}/profil/${memberId}/einstellungen` : `${baseUrls.legacy_profile}/einstellungen`,
		};
	}

	/**
	 * Click handler for the notifications tab change
	 */
	function clickNotificationsTab() {
		trackUserAction(
			$notifications.hasNewNotifications ? 'header_tabchange_notifications_new' : 'header_tabchange_notifications',
		);
		removeNewNotificationsIndicator();
	}

	$: isPurUser = ($loginState.payload?.['cognito:groups'] as string[])?.includes('pur') || false;
	$: isSmartUser = ($loginState.payload?.['cognito:groups'] as string[])?.includes('sne') || false;
	$: isSupporter = ($loginState.payload?.['cognito:groups'] as string[])?.includes('sup') || false;
	$: hasEpaperEntitlement =
		($loginState.payload?.['cognito:groups'] as string[])?.includes('eat') ||
		($loginState.payload?.['cognito:groups'] as string[])?.includes('epa') ||
		false;
	$: isPrintUser =
		(($loginState.payload?.['cognito:groups'] as string[])?.includes('sco') &&
			!($loginState.payload?.['cognito:groups'] as string[])?.includes('sne')) ||
		false;
	/**
	 *
	 * @param node
	 */
	function mutation(node: HTMLElement) {
		observe(node);

		return {
			destroy() {
				disconnect();
			},
		};
	}
</script>

<div class="dst-useractions" bind:this={root} use:mutation>
	<!-- Wait for initial user-state -->
	{#if typeof $loginState.isLoggedIn !== 'undefined'}
		{#if $loginState.isLoggedIn}
			<button
				aria-expanded={ariaExpanded}
				type="button"
				title="Profil"
				data-closable-target="app-useractions"
				on:click={() => trackUserAction('header_showprofile')}
			>
				<span>Profil</span>
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{#if isPurUser || isSmartUser || isPrintUser || hasEpaperEntitlement}
						{@html dstHeaderAccountAbo}
					{:else if isSupporter}
						{@html dstHeaderAccountSupporter}
					{:else}
						{@html dstHeaderAccount}
					{/if}
				</svg>
			</button>
		{:else}
			<button type="button" title="Anmelden" on:click={handleLogin}>
				<span>Anmelden</span>
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{@html dstHeaderKey}
				</svg>
			</button>
		{/if}
	{/if}

	<dst-flyout
		id="app-useractions"
		data-closable="app-useractions"
		data-closable-active="false"
		bind:this={userActionsFlyout}
	>
		<div class="flyout--tabs" slot="tabs">
			<button type="button" disabled>Profil</button>
			<button
				type="button"
				data-closable-target="app-notifications"
				data-closable-close="true"
				on:click={clickNotificationsTab}
			>
				Mitteilungen

				<dst-counter />
			</button>
		</div>

		<div class="app--header--useractions--menus u-scrollbar">
			{#if appid === 'editorialweb' || appid === 'livecontent' || appid === 'userprofile'}
				<nav aria-label="Benutzer-Inhalte" class="flyout--menu">
					<a href={profileUrls.postings}>Postings</a>
					<a href={profileUrls.followees}>Mitposter:innen</a>
					<!-- TODO: Implement Push Settings -->
					<!-- <button data-closable-target="dialog-cleverpush-settings">Push-Einstellungen</button> -->
				</nav>
			{/if}

			<nav aria-label="Benutzerkonto" class="flyout--menu">
				<a
					href="{baseUrl}/profil"
					class="flyout--menu--item-complex"
					on:click={() => trackUserAction('header_showprofile')}
				>
					<small>Angemeldet als</small>
					<strong>{$loginState?.payload?.email || ''}</strong>
				</a>

				{#if isSupporter || isPurUser || isSmartUser}
					<a href={import.meta.env.VITE_BASE_URL_ABOSERVICE} class="flyout--menu--item-complex">
						<small>Digital-Abos und Unterstützungen verwalten</small>

						{#if isSmartUser}
							<span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
									><path fill="currentColor" d={dstAbo} /></svg
								>
								<strong>DER STANDARD Smart</strong>
							</span>
						{:else if isPurUser}
							<span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
									><path fill="currentColor" d={dstAbo} /></svg
								>
								<strong>derStandard.at PUR</strong>
							</span>
						{/if}

						{#if isSupporter}
							<span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<!-- eslint-disable-next-line svelte/no-at-html-tags -->
									{@html dstSupporterThickColor}
								</svg>
								<strong>Aktive Unterstützung</strong>
							</span>
						{/if}
					</a>
				{/if}

				{#if hasEpaperEntitlement}
					<span class="flyout--menu--item-complex">
						<small>DER STANDARD ePaper-Abo</small>
						<a href="{import.meta.env.VITE_BASE_URL_ABO}/aboservice">
							<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
								><path fill="currentColor" d={dstAbo} /></svg
							>
							<strong>Aboservice</strong>
						</a>
						<a href={baseUrls.epaper_default}>
							<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
								><path fill="currentColor" d={mdiNewspaper} /></svg
							>
							<strong>DER STANDARD ePaper lesen</strong>
						</a>
					</span>
				{/if}

				<a href={profileUrls.settings}>Einstellungen</a>
				<button type="button" title="Abmelden" on:click={handleLogout}>Abmelden</button>
			</nav>
		</div>
	</dst-flyout>
</div>

<style lang="scss" global>
	@use './DstUseractions.scss';
</style>
